import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { surveyLocalization } from "survey-core";

interface LanguageSelectorProps {
  currentLocale: string;
  availableLocales: string[];
  onLocaleChange: (locale: string) => void;
}

// Display names for respondent-facing language selector (native names with proper capitalization)
const LOCALE_DISPLAY_NAMES: Record<string, string> = {
  ar: "العربية",
  eu: "Euskara",
  bg: "Български",
  mm: "မြန်မာ",
  ca: "Català",
  hr: "Hrvatski",
  cs: "Čeština",
  da: "Dansk",
  nl: "Nederlands",
  "nl-BE": "Vlaams",
  en: "English",
  et: "Eesti",
  fi: "Suomi",
  fr: "Français",
  ka: "ქართული",
  de: "Deutsch",
  gr: "Ελληνικά",
  ht: "Kreyòl Ayisyen",
  he: "עברית",
  hi: "हिन्दी",
  hu: "Magyar",
  is: "Íslenska",
  id: "Bahasa Indonesia",
  it: "Italiano",
  ja: "日本語",
  kk: "Қазақ",
  km: "ខ្មែរ",
  ko: "한국어",
  lv: "Latviešu",
  lt: "Lietuvių",
  mk: "Македонски",
  ms: "Bahasa Melayu",
  no: "Norsk",
  fa: "فارسی",
  fil: "Filipino",
  pl: "Polski",
  pt: "Português",
  "pt-br": "Português Brasileiro",
  ro: "Română",
  ru: "Русский",
  rs: "Српски",
  "zh-cn": "简体中文",
  sk: "Slovenčina",
  sl: "Slovenščina",
  es: "Español",
  sw: "Kiswahili",
  sv: "Svenska",
  tg: "Тоҷикӣ",
  tel: "తెలుగు",
  th: "ไทย",
  "zh-tw": "繁體中文",
  tr: "Türkçe",
  uk: "Українська",
  ur: "اردو",
  vi: "Tiếng Việt",
  cy: "Cymraeg",
};

const mapApiLocaleToSurveyJsLocale = (apiLocale: string): string => {
  if (apiLocale === "default") return "en";
  return apiLocale;
};

const getLocaleDisplayName = (localeCode: string): string => {
  if (LOCALE_DISPLAY_NAMES[localeCode]) return LOCALE_DISPLAY_NAMES[localeCode];
  const locale = surveyLocalization.locales[localeCode];
  if (locale?.nativeName) return locale.nativeName;
  return localeCode;
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLocale,
  availableLocales,
  onLocaleChange,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    const newLocale = event.target.value;
    onLocaleChange(newLocale);
  };

  if (!availableLocales || availableLocales.length <= 1) {
    return null;
  }

  return (
    <FormControl
      sx={{
        position: "fixed",
        top: 16,
        right: 32,
        minWidth: 120,
        zIndex: 1000,
        backgroundColor: "white",
        borderRadius: 1,
        "@media (max-width: 768px)": {
          top: 8,
          right: "12%",
          minWidth: 85,
          maxWidth: 105,
        },
        "@media (max-width: 480px)": {
          top: 8,
          right: "12%",
          minWidth: 75,
          maxWidth: 95,
        },
        "& .MuiOutlinedInput-root": {
          fontSize: "14px",
          "@media (max-width: 768px)": {
            fontSize: "12px",
          },
          "@media (max-width: 480px)": {
            fontSize: "11px",
          },
          "& fieldset": {
            borderColor: "darkgray",
          },
          "&:hover fieldset": {
            borderColor: "darkgray",
          },
          "&.Mui-focused fieldset": {
            borderColor: "darkgray",
          },
        },
        "& .MuiSelect-select": {
          paddingTop: "8px",
          paddingBottom: "8px",
          "@media (max-width: 768px)": {
            paddingTop: "6px",
            paddingBottom: "6px",
          },
          "@media (max-width: 480px)": {
            paddingTop: "5px",
            paddingBottom: "5px",
          },
        },
      }}
    >
      <Select
        value={currentLocale}
        onChange={handleChange}
        displayEmpty
        size="small"
        MenuProps={{
          disableScrollLock: true,
        }}
      >
        {availableLocales.map((apiLocale) => {
          const code = mapApiLocaleToSurveyJsLocale(apiLocale);
          return (
            <MenuItem key={code} value={code}>
              {getLocaleDisplayName(code)}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
